import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Cookie } from 'lucide-react';
import CookieSettings from './CookieSettings';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// Define the consent types
export type CookieConsentOptions = {
  necessary: boolean;
  preferences: boolean;
};

// Default options
export const defaultConsentOptions: CookieConsentOptions = {
  necessary: true,
  preferences: false,
};

const CookieConsent: React.FC = () => {
  const { t } = useTranslation('cookies');
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [consentOptions, setConsentOptions] = useState<CookieConsentOptions>(defaultConsentOptions);

  const saveConsent = (options: CookieConsentOptions) => {
    localStorage.setItem('cookieConsent', JSON.stringify(options));
    localStorage.setItem('cookieConsentTimestamp', new Date().toISOString());
  };

  const handleAcceptAll = () => {
    const allAccepted: CookieConsentOptions = { necessary: true, preferences: true };
    setConsentOptions(allAccepted);
    saveConsent(allAccepted);
    setShowBanner(false);
    toast.success(t('banner.allAccepted'));
  };

  const handleRejectAll = () => {
    setConsentOptions(defaultConsentOptions);
    saveConsent(defaultConsentOptions);
    setShowBanner(false);
    toast.success(t('banner.onlyNecessary'));
  };

  const handleSaveSettings = (options: CookieConsentOptions) => {
    setConsentOptions(options);
    saveConsent(options);
    setShowSettings(false);
    setShowBanner(false);
    toast.success(t('banner.saved'));
  };

  const handleForgetMe = () => {
    localStorage.removeItem('cookieConsent');
    localStorage.removeItem('cookieConsentTimestamp');
    setConsentOptions(defaultConsentOptions);
    setShowBanner(true);
    setShowSettings(false);
    toast.success(t('banner.forgotten'));
  };

  useEffect(() => {
    const storedConsent = localStorage.getItem('cookieConsent');
    if (storedConsent) {
      try {
        setConsentOptions(JSON.parse(storedConsent));
      } catch {
        setShowBanner(true);
      }
    } else {
      setShowBanner(true);
    }
  }, []);

  return (
    <>
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background border-t border-border shadow-lg animate-fade-in">
          <div className="container mx-auto">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-2">{t('banner.title')}</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  {t('banner.description')}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setShowSettings(true)}>
                  {t('banner.settings')}
                </Button>
                <Button variant="outline" onClick={handleRejectAll}>
                  {t('banner.rejectAll')}
                </Button>
                <Button onClick={handleAcceptAll}>
                  {t('banner.acceptAll')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSettings && (
        <CookieSettings
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          onSave={handleSaveSettings}
          onForgetMe={handleForgetMe}
          initialOptions={consentOptions}
        />
      )}

      {!showBanner && (
        <Button
          variant="outline"
          className="fixed bottom-4 right-4 rounded-full p-2 shadow-lg z-50 bg-background"
          onClick={() => setShowSettings(!showSettings)}
          title={t('banner.settings')}
        >
          <Cookie className="h-6 w-6" />
        </Button>
      )}
    </>
  );
};

export default CookieConsent;
